/**
 * Roadmap App Main Component (Enhanced)
 * 
 * Features:
 * - Detail modal on card click
 * - Direct upvoting from cards
 * - Search and filters
 * - Auth modal for guest voting
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import RoadmapColumn from './components/RoadmapColumn';
import RoadmapFilters from './components/RoadmapFilters';
import RoadmapDetailModal from './components/RoadmapDetailModal';
import AuthModal from '../components/AuthModal';

const RoadmapApp = ({ config }) => {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('top');
    const [selectedProduct, setSelectedProduct] = useState(config.product || '');
    const [selectedItem, setSelectedItem] = useState(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    
    // Check if user is logged in
    const isLoggedIn = config.isLoggedIn;
    const currentUser = config.currentUser;
    
    // Auth settings from config
    const enableGoogleLogin = config.enableGoogleLogin;
    const enableEmailSignup = config.enableEmailSignup;
    const loginUrl = config.loginUrl;
    const registerUrl = config.registerUrl;
    const ajaxUrl = config.ajaxUrl;
    
    const columns = config.columns || {
        planned: { label: 'Planned', color: '#3b82f6' },
        in_progress: { label: 'In Progress', color: '#f59e0b' },
        completed: { label: 'Completed', color: '#10b981' },
        rejected: { label: 'Rejected', color: '#ef4444' },
    };

    // Fetch roadmap items
    useEffect(() => {
        const fetchRoadmap = async () => {
            setLoading(true);
            setError(null);
            
            try {
                let path = '/changeloger/v1/roadmap';
                const params = new URLSearchParams();
                
                if (selectedProduct) {
                    params.append('product', selectedProduct);
                }
                if (searchTerm) {
                    params.append('search', searchTerm);
                }
                
                if (params.toString()) {
                    path += '?' + params.toString();
                }
                
                const response = await apiFetch({ path });
                setItems(response.items || []);
                setStats(response.stats || {});
            } catch (err) {
                console.error('Failed to fetch roadmap:', err);
                setError(__( 'Failed to load roadmap. Please try again.', 'changeloger-pro' ));
            } finally {
                setLoading(false);
            }
        };
        
        fetchRoadmap();
    }, [selectedProduct, searchTerm]);

    // Handle upvote - show auth modal for guests
    const handleUpvote = async (itemId) => {
        if (!isLoggedIn) {
            setShowAuthModal(true);
            return;
        }

        // Find the current item to get its current vote state
        const currentItem = items.find(item => item.id === itemId);
        if (!currentItem) return;

        // Optimistic update - immediately toggle the vote state
        const newHasVoted = !currentItem.has_voted;
        const voteChange = newHasVoted ? 1 : -1;
        
        setItems(prev => prev.map(item => {
            if (item.id === itemId) {
                return {
                    ...item,
                    votes: (item.votes || 0) + voteChange,
                    has_voted: newHasVoted
                };
            }
            return item;
        }));

        // Update selected item if modal is open
        if (selectedItem && selectedItem.id === itemId) {
            setSelectedItem(prev => ({
                ...prev,
                votes: (prev.votes || 0) + voteChange,
                has_voted: newHasVoted
            }));
        }

        try {
            const response = await apiFetch({
                path: `/changeloger/v1/feedback/${itemId}/upvote`,
                method: 'POST'
            });

            // Sync with server response if available
            if (response && (response.net_votes !== undefined || response.count !== undefined)) {
                setItems(prev => prev.map(item => {
                    if (item.id === itemId) {
                        return {
                            ...item,
                            votes: response.net_votes ?? response.count ?? item.votes,
                            upvotes: response.upvotes ?? response.count,
                            downvotes: response.downvotes ?? 0,
                            has_voted: response.action === 'added'
                        };
                    }
                    return item;
                }));

                if (selectedItem && selectedItem.id === itemId) {
                    setSelectedItem(prev => ({
                        ...prev,
                        votes: response.net_votes ?? response.count ?? prev.votes,
                        upvotes: response.upvotes ?? response.count,
                        downvotes: response.downvotes ?? 0,
                        has_voted: response.action === 'added'
                    }));
                }
            }
        } catch (err) {
            console.error('Failed to upvote:', err);
            // Revert optimistic update on error
            setItems(prev => prev.map(item => {
                if (item.id === itemId) {
                    return {
                        ...item,
                        votes: (item.votes || 0) - voteChange,
                        has_voted: !newHasVoted
                    };
                }
                return item;
            }));

            if (selectedItem && selectedItem.id === itemId) {
                setSelectedItem(prev => ({
                    ...prev,
                    votes: (prev.votes || 0) - voteChange,
                    has_voted: !newHasVoted
                }));
            }
        }
    };

    // Handle card click to open modal
    const handleCardClick = (item) => {
        setSelectedItem(item);
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    const handleProductChange = (productId) => {
        setSelectedProduct(productId);
    };

    const getItemsByStatus = (status) => {
        const filtered = items.filter(item => item.status === status);
        
        // Apply sorting based on sortBy state
        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'top':
                    // Sort by votes (highest first)
                    return (b.votes || 0) - (a.votes || 0);
                case 'new':
                    // Sort by date (newest first)
                    const dateA = new Date(a.date || 0);
                    const dateB = new Date(b.date || 0);
                    return dateB - dateA;
                case 'trending':
                    // Trending: combination of recency and votes
                    // Items with more recent activity and votes rank higher
                    const now = new Date();
                    const ageA = (now - new Date(a.date || 0)) / (1000 * 60 * 60 * 24); // days
                    const ageB = (now - new Date(b.date || 0)) / (1000 * 60 * 60 * 24);
                    // Score = votes / (age + 2)^1.5 (gravity algorithm similar to Hacker News)
                    const scoreA = (a.votes || 0) / Math.pow(ageA + 2, 1.5);
                    const scoreB = (b.votes || 0) / Math.pow(ageB + 2, 1.5);
                    return scoreB - scoreA;
                default:
                    return 0;
            }
        });
        
        return sorted;
    };

    if (error) {
        return (
            <div className="cha-roadmap-error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>{__( 'Retry', 'changeloger-pro' )}</button>
            </div>
        );
    }

    return (
        <div className="cha-roadmap-frontend">
            <RoadmapFilters 
                selectedProduct={selectedProduct}
                searchTerm={searchTerm}
                onProductChange={handleProductChange}
                onSearch={handleSearch}
                sortBy={sortBy}
                onSortChange={setSortBy}
            />
            
            <div className="cha-roadmap-board" style={{ gridTemplateColumns: `repeat(${ config.columnsPerRow || 4 }, 1fr)` }}>
                {Object.entries(columns).map(([status, columnConfig]) => (
                    <RoadmapColumn
                        key={status}
                        status={status}
                        label={columnConfig.label}
                        color={columnConfig.color}
                        highlighted={columnConfig.highlighted}
                        items={getItemsByStatus(status)}
                        count={stats[status] || 0}
                        loading={loading}
                        onCardClick={handleCardClick}
                        onUpvote={handleUpvote}
                        isLoggedIn={isLoggedIn}
                        showVoteCount={config.showVoteCount}
                        onAuthRequired={() => setShowAuthModal(true)}
                    />
                ))}
            </div>

            {/* Detail Modal */}
            {selectedItem && (
                <RoadmapDetailModal
                    item={selectedItem}
                    columns={columns}
                    onClose={() => setSelectedItem(null)}
                    onUpvote={handleUpvote}
                    isLoggedIn={isLoggedIn}
                    currentUser={currentUser}
                    onAuthRequired={() => setShowAuthModal(true)}
                />
            )}

            {/* Auth Modal */}
            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                onLoginSuccess={() => window.location.reload()}
                enableGoogleLogin={enableGoogleLogin}
                enableEmailSignup={enableEmailSignup}
                loginUrl={loginUrl}
                registerUrl={registerUrl}
                ajaxUrl={ajaxUrl}
            />
        </div>
    );
};

export default RoadmapApp;
